import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, isOwner } from '../lib/roles';
import { Button } from './ui/button';
import { LogOut } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', testId: 'nav-dashboard' },
  { path: '/magic-create', label: 'Magic Create', testId: 'nav-magic-create', isMagic: true },
  { path: '/campaigns', label: 'My Campaigns', testId: 'nav-campaigns' },
  { path: '/templates', label: 'Templates', testId: 'nav-templates' },
  { path: '/reel-editor', label: 'Reels', testId: 'nav-reels' },
  { path: '/carousel-editor', label: 'Carousels', testId: 'nav-carousels' },
  { path: '/print-center', label: 'Print Center', testId: 'nav-print-center' },
  { path: '/email-lists', label: 'Email Lists', testId: 'nav-email-lists' },
  { path: '/social-accounts', label: 'Connect Accounts', testId: 'nav-social-accounts' },
];

const ADMIN_ITEM = { path: '/design-studio', label: 'Design Studio', testId: 'nav-admin' };
const SUPPORT_ITEM = { path: '/admin/support', label: 'Support Inbox', testId: 'nav-admin-support' };
const OWNER_ITEM = { path: '/admin/users', label: 'Founder', testId: 'nav-owner' };

export const AppNavbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-gray-200" data-testid="app-header">
        <div className="max-w-screen-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>RealProp</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/profile"
              className="text-sm text-gray-500 hover:text-black transition-colors font-medium"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="nav-user-name"
              title="Profile & settings"
            >
              {user?.name}
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 text-gray-500 hover:text-black" data-testid="nav-logout-btn">
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-14 z-40" data-testid="app-nav">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="flex items-center gap-1 h-11 overflow-x-auto scrollbar-hide">
            {NAV_ITEMS.map(item => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                    active
                      ? 'bg-black text-white'
                      : item.isMagic
                        ? 'text-purple-600 hover:bg-purple-50'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-black'
                  }`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                  data-testid={item.testId}
                >
                  {item.label}
                </Link>
              );
            })}
            {isAdmin(user) && (
              <Link
                to={ADMIN_ITEM.path}
                className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                  isActive(ADMIN_ITEM.path)
                    ? 'bg-orange-500 text-white'
                    : 'text-orange-500 hover:bg-orange-50'
                }`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid={ADMIN_ITEM.testId}
              >
                {ADMIN_ITEM.label}
              </Link>
            )}
            {isAdmin(user) && (
              <Link
                to={SUPPORT_ITEM.path}
                className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                  isActive(SUPPORT_ITEM.path)
                    ? 'bg-emerald-600 text-white'
                    : 'text-emerald-600 hover:bg-emerald-50'
                }`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid={SUPPORT_ITEM.testId}
              >
                {SUPPORT_ITEM.label}
              </Link>
            )}
            {isOwner(user) && (
              <Link
                to={OWNER_ITEM.path}
                className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                  isActive(OWNER_ITEM.path)
                    ? 'bg-yellow-500 text-white'
                    : 'text-yellow-600 hover:bg-yellow-50'
                }`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid={OWNER_ITEM.testId}
              >
                {OWNER_ITEM.label}
              </Link>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default AppNavbar;
